import React, { useEffect } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DataTable from 'src/components/ui/data-table';
import { Button } from 'src/components/ui/button';
import { Card, CardContent } from 'src/components/ui/card';
import { useOrganizations } from 'src/services/api/queries/organizationQueries';
import { Calendar, Building2, UserPlus } from 'lucide-react';

const DEFAULT_PAGE_SIZE = 10;

const NameCell = ({ row }) => {
  const { name, org_id: orgId } = row.original;
  return (
    <div>
      <p className="text-[13px] font-[550] text-foreground-strong">{name}</p>
      {orgId && (
        <p className="font-mono text-[11.5px] text-muted-foreground tracking-[.02em] mt-0.5">
          {orgId}
        </p>
      )}
    </div>
  );
};

const CategoryCell = ({ value }) => {
  if (!value) {
    return (
      <span className="text-[12.5px] text-muted-foreground italic">N/A</span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-semibold tracking-wide uppercase bg-muted text-gray-500 border border-gray-200 whitespace-nowrap">
      {value}
    </span>
  );
};

const ActiveStatusCell = ({ value }) => (
  <StatusBadge value={value ? 'ACTIVE' : 'INACTIVE'} />
);

const DateCell = ({ value }) => {
  if (!value)
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  const formatted = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return (
    <div className="flex items-center gap-1.5">
      <Calendar
        className="w-3.5 h-3.5 text-muted-foreground"
        strokeWidth={1.8}
      />
      <span className="text-[12.5px] text-gray-500">{formatted}</span>
    </div>
  );
};

const ManageUsersCell = ({ row, onNavigate }) => (
  <Button
    variant="ghost"
    size="icon"
    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-md hover:bg-brand-bg-subtle hover:text-primary"
    title="Manage Users"
    onClick={(e) => {
      e.stopPropagation();
      onNavigate(row.original);
    }}
  >
    <UserPlus className="h-3.5 w-3.5 text-gray-500" />
  </Button>
);

const getColumns = (onNavigate) => [
  {
    key: 'name',
    header: 'Organization Name',
    minWidth: '240px',
    render: (_v, row) => <NameCell row={{ original: row }} />,
  },
  {
    key: 'customer_category',
    header: 'Category',
    width: '180px',
    minWidth: '140px',
    render: (v) => <CategoryCell value={v} />,
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (v) => <ActiveStatusCell value={v} />,
  },
  {
    key: 'created_at',
    header: 'Created',
    width: '150px',
    minWidth: '130px',
    render: (v) => <DateCell value={v} />,
  },
  {
    key: 'actions',
    header: '',
    width: '70px',
    minWidth: '60px',
    render: (_v, row) => (
      <ManageUsersCell row={{ original: row }} onNavigate={onNavigate} />
    ),
  },
];

const OrganizationUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const {
    data: organizationsData,
    isLoading,
    error,
    refetch,
  } = useOrganizations({
    pageNo: currentPage,
    pageSize: currentPageSize,
  });

  const organizations = organizationsData?.data?.items || [];
  const totalItems = organizationsData?.data?.pagination?.total || 0;

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  const handleNavigate = (row) => {
    navigate(
      `/organization-users/${row.id}?name=${encodeURIComponent(row.name)}&orgId=${row.org_id}`
    );
  };

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = getColumns(handleNavigate);
  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));

  const emptyState = (
    <div className="bg-card rounded-xl border border-gray-100 dark:border-border shadow-sm flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <Building2
          className="w-7 h-7 text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No organisations found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs leading-relaxed">
        No organisations are available at this time.
      </p>
    </div>
  );

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading organizations: {error.message || error}</p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Organization Users" />

      <PageContainer className="space-y-5">
        <div>
          <p className="text-[13.5px] text-gray-500 mt-1 text-left">
            Select an organisation to manage its users and access permissions.
          </p>
        </div>

        <DataTable
          className="rounded-xl overflow-hidden"
          data={organizations}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onRowClick={(row) => handleNavigate(row)}
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors cursor-pointer'
          }
        />
      </PageContainer>
    </div>
  );
};

export default OrganizationUsers;
